import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { fromLocalInput, toLocalInput } from '@/calendar/dates'
import { ApiError } from '@/lib/api'
import {
  acceptProposal,
  dismissProposal,
  fetchMyProposals,
  fetchRecordProposals,
  type ProposalChanges,
  type TaskProposal,
} from './taskProposalsApi'

/**
 * «Предложено ИИ» (тикет #131): задачи, которые ИИ нашёл в письмах, звонках и комментариях.
 *
 * Предложение — не задача: задача появляется по «Подтвердить» (одно нажатие, без формы), по
 * «Изменить и подтвердить» (тема, срок, исполнитель) или не появляется вовсе («Отклонить»).
 *
 * Чего экран не делает сам:
 * - срок не считает — его посчитал сервер от момента письма в поясе компании; без срока
 *   показывается причина словами, а не пустое место;
 * - не решает, можно ли подтвердить одним нажатием: флаг `assignable` пришёл с сервера (область
 *   `sales.activity:create` смотрящего), и без него кнопка «Подтвердить» не рисуется — вместо
 *   неё форма с выбором исполнителя, которого смотрящий вправе назначить;
 * - не выдаёт пересказ за цитату: основание, не найденное в тексте дословно, подписано так.
 */

const ENTITY_PATH: Record<string, string> = {
  deal: '/deals',
  lead: '/leads',
  counterparty: '/counterparties',
  contact: '/contacts',
  invoice: '/invoices',
}

type Props = { entityType: string; entityId: string } | { entityType?: undefined; entityId?: undefined }

/** Список на «Моём дне» (без параметров) или на карточке записи. */
export function TaskProposalsPanel(props: Props) {
  const { t, user } = useApp()
  const onCard = props.entityType != null
  const key = ['task-proposals', props.entityType ?? 'mine', props.entityId ?? '']
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      onCard
        ? fetchRecordProposals(props.entityType as string, props.entityId as string, signal)
        : fetchMyProposals(signal).then((mine) => mine.proposals),
    retry: false,
    enabled: can(user, 'sales.activity', 'read'),
  })
  if (!can(user, 'sales.activity', 'read')) return null
  const proposals = query.data ?? []
  // Пустой блок не рисуется: «ИИ ничего не предложил» каждый день — шум, к которому привыкают и
  // перестают смотреть на блок. Ошибка при этом говорится словами — её с пустотой не спутать.
  if (!query.isError && proposals.length === 0) return null
  return (
    <section className="panel" aria-labelledby="ai-proposals-h">
      <div className="panel-h">
        <span className="h2" id="ai-proposals-h">{t('proposals.title')}</span>
        {query.isSuccess && <span className="count-pill">{proposals.length}</span>}
      </div>
      <div className="panel-b stack" style={{ gap: 10 }}>
        {query.isError ? (
          <p role="alert" className="t-sm">
            {query.error instanceof ApiError ? query.error.message : t('proposals.error')}
          </p>
        ) : (
          proposals.map((proposal) => <ProposalRow key={proposal.id} proposal={proposal} onCard={onCard} />)
        )}
      </div>
    </section>
  )
}

function ProposalRow({ proposal, onCard }: { proposal: TaskProposal; onCard: boolean }) {
  const { t, locale, user } = useApp()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const mayDecide = can(user, 'sales.activity', 'create')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['task-proposals'] })
    void queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
  }
  const failure = (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error'))
  const accept = useMutation({
    mutationFn: (changes?: ProposalChanges) => acceptProposal(proposal.id, changes),
    onSuccess: () => {
      setNotice(null)
      setEditing(false)
      refresh()
    },
    onError: failure,
  })
  const dismiss = useMutation({
    mutationFn: () => dismissProposal(proposal.id),
    onSuccess: refresh,
    onError: failure,
  })
  const busy = accept.isPending || dismiss.isPending

  return (
    <article className="stack" style={{ gap: 4 }} aria-label={proposal.subject}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span className="strong">{proposal.subject}</span>
        <span className={proposal.dueDate == null ? 't-sm muted' : 't-sm'}>
          {proposal.dueDate == null
            ? t('proposals.noDue', { reason: proposal.dueNote ?? '' })
            : t('proposals.due', { date: day(proposal.dueAt ?? proposal.dueDate, locale) })}
        </span>
        {!onCard && proposal.counterpartyName != null && (
          <span className="t-sm secondary">{proposal.counterpartyName}</span>
        )}
      </div>
      <span className="t-sm">
        {proposal.basisVerified
          ? t('proposals.basis', { text: proposal.basis })
          : t('proposals.basisUnverified', { text: proposal.basis })}{' '}
        <SourceLink proposal={proposal} onCard={onCard} />
      </span>
      <span className="t-sm secondary">
        {proposal.assigneeName != null
          ? t('proposals.assignee', { name: proposal.assigneeName })
          : t('proposals.noAssignee')}
        {proposal.assigneeReason != null ? ` — ${proposal.assigneeReason}` : ''}
      </span>
      {notice && <p role="alert" className="t-sm" style={{ margin: 0 }}>{notice}</p>}
      {mayDecide && !editing && (
        <div className="row" style={{ gap: 6 }}>
          {proposal.assignable ? (
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => accept.mutate(undefined)}>
              {t('proposals.accept')}
            </button>
          ) : (
            <span className="t-sm muted">{t('proposals.notAssignable')}</span>
          )}
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setEditing(true)}>
            {t('proposals.edit')}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => dismiss.mutate()}>
            {t('proposals.dismiss')}
          </button>
        </div>
      )}
      {mayDecide && editing && (
        <EditForm
          proposal={proposal}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSubmit={(changes) => accept.mutate(changes)}
        />
      )}
    </article>
  )
}

function EditForm({
  proposal,
  busy,
  onCancel,
  onSubmit,
}: {
  proposal: TaskProposal
  busy: boolean
  onCancel: () => void
  onSubmit: (changes: ProposalChanges) => void
}) {
  const { t, user } = useApp()
  const choices = (proposal.alternatives ?? []).filter((choice) => choice.assignable)
  const me = user?.employeeId ?? null
  const initialAssignee = proposal.assignable
    ? (proposal.assigneeEmployeeId ?? '')
    : (choices[0]?.employeeId ?? me ?? '')
  const [subject, setSubject] = useState(proposal.subject)
  const [due, setDue] = useState(proposal.dueAt != null ? toLocalInput(new Date(proposal.dueAt)) : '')
  const [assignee, setAssignee] = useState(initialAssignee)
  const options = [...choices]
  if (me != null && !options.some((choice) => choice.employeeId === me)) {
    options.push({ employeeId: me, name: t('proposals.me'), role: 'owner', load: 0, history: 0, assignable: true })
  }

  return (
    <form
      className="stack"
      style={{ gap: 6 }}
      onSubmit={(event) => {
        event.preventDefault()
        const changes: ProposalChanges = {}
        if (subject.trim() !== '' && subject.trim() !== proposal.subject) changes.subject = subject.trim()
        const moment = fromLocalInput(due)
        if (moment != null) changes.dueAt = moment.toISOString()
        if (assignee !== '') changes.assigneeEmployeeId = assignee
        onSubmit(changes)
      }}
    >
      <label className="stack" style={{ gap: 2 }}>
        <span className="t-sm">{t('proposals.form.subject')}</span>
        <input className="input" value={subject} maxLength={512} onChange={(event) => setSubject(event.target.value)} />
      </label>
      <label className="stack" style={{ gap: 2 }}>
        <span className="t-sm">{t('proposals.form.due')}</span>
        <input className="input" type="datetime-local" value={due} onChange={(event) => setDue(event.target.value)} />
      </label>
      <label className="stack" style={{ gap: 2 }}>
        <span className="t-sm">{t('proposals.form.assignee')}</span>
        <select className="input" value={assignee} onChange={(event) => setAssignee(event.target.value)}>
          {options.map((choice) => (
            <option key={choice.employeeId} value={choice.employeeId}>
              {choice.name}
              {choice.onLeaveUntil != null ? ` — ${t('proposals.onLeave', { until: choice.onLeaveUntil })}` : ''}
              {` · ${t('proposals.load', { count: choice.load })}`}
            </option>
          ))}
        </select>
      </label>
      <div className="row" style={{ gap: 6 }}>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || assignee === ''}>
          {t('proposals.form.submit')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          {t('proposals.form.cancel')}
        </button>
      </div>
    </form>
  )
}

/** Ссылка на основание: на карточке — к событию ленты, на «Моём дне» — на карточку записи. */
function SourceLink({ proposal, onCard }: { proposal: TaskProposal; onCard: boolean }) {
  const { t, locale } = useApp()
  const kind = proposal.source.kind === 'email'
    ? (proposal.source.direction === 'outbound' ? 'emailOut' : 'emailIn')
    : proposal.source.kind
  const label = t(`proposals.source.${kind}`, { date: day(proposal.source.at, locale) })
  const anchor = `#feed-event-${proposal.source.id}`
  if (onCard) {
    return <a href={anchor} className="chip">{label}</a>
  }
  const base = proposal.entityType != null ? ENTITY_PATH[proposal.entityType] : undefined
  if (base == null || proposal.entityId == null) return <span className="chip">{label}</span>
  return <Link className="chip" to={`${base}/${proposal.entityId}${anchor}`}>{label}</Link>
}

function day(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  })
}
